import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Badge } from "react-bootstrap";
import Select from "react-select";
import { FaPlus, FaTrash } from "react-icons/fa";
import { useAddClientsToField, useRemoveClientsFromField } from "../../../hooks/CHAMPS/useFieldClients";
import { useClients } from "../../../hooks/useGetClients";
import { toast } from "react-toastify";

const ManageFieldClients = ({ show, setShow, champ }) => {
  const addClientsMutation = useAddClientsToField();
  const removeClientsMutation = useRemoveClientsFromField();

  const [selectedClients, setSelectedClients] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [clientSearch, setClientSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setClientSearch(inputValue), 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data: clientsData } = useClients(1, 50, clientSearch);

  // Utiliser directement les clients du champ au lieu d'un état local
  const currentClients = champ?.clients || [];

  const handleClose = () => {
    setShow(false);
    setSelectedClients([]);
  };

  const handleAddClients = () => {
    if (selectedClients.length === 0) {
      toast.error("Veuillez sélectionner au moins un client");
      return;
    }

    const clientIds = selectedClients.map(c => c.value);

    addClientsMutation.mutate(
      { id: champ.id, clientIds },
      {
        onSuccess: () => {
          toast.success(`${clientIds.length} client(s) ajouté(s) avec succès`);
          setSelectedClients([]);
        },
        onError: (error) => {
          toast.error(error.message || "Erreur lors de l'ajout des clients");
        },
      }
    );
  };

  const handleRemoveClient = (clientId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir retirer ce client ?")) {
      return;
    }

    removeClientsMutation.mutate(
      { id: champ.id, clientIds: [clientId] },
      {
        onSuccess: () => {
          toast.success("Client retiré avec succès");
        },
        onError: (error) => {
          toast.error(error.message || "Erreur lors du retrait du client");
        },
      }
    );
  };

  if (!champ) return null;

  const allClients = clientsData?.data?.clients || [];
  const currentClientIds = currentClients.map(c => c.id);
  const availableClients = allClients.filter(c => !currentClientIds.includes(c.id));

  const clientOptions = availableClients.map(client => ({
    value: client.id,
    label: client.name,
  }));

  const isPending = addClientsMutation.isPending || removeClientsMutation.isPending;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Gérer les clients : {champ.label}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {champ.all_clients ? (
          <div className="alert alert-info">
            Ce champ est configuré pour <strong>tous les clients</strong>. 
            Désactivez cette option dans les paramètres du champ pour gérer des clients spécifiques.
          </div>
        ) : (
          <>
            <div className="mb-4">
              <h6>Ajouter des clients</h6>
              <div className="d-flex gap-2 align-items-end">
                <div style={{ flex: 1 }}>
                  <Select
                    isMulti
                    value={selectedClients}
                    onChange={setSelectedClients}
                    options={clientOptions}
                    onInputChange={(value) => setInputValue(value)}
                    filterOption={null}
                    placeholder="Rechercher un client..."
                    className="basic-multi-select"
                    classNamePrefix="select"
                    isDisabled={isPending}
                  />
                </div>
                <Button
                  variant="success"
                  onClick={handleAddClients}
                  disabled={isPending || selectedClients.length === 0}
                >
                  <FaPlus className="me-2" />
                  Ajouter
                </Button>
              </div>
            </div>

            <h6>Clients actuels ({currentClients.length})</h6>
            {currentClients.length === 0 ? (
              <p className="text-muted text-center">Aucun client associé</p>
            ) : (
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Adresse</th>
                    <th>Statut</th>
                    <th style={{ width: "100px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentClients.map((client) => (
                    <tr key={client.id}>
                      <td><strong>{client.name}</strong></td>
                      <td className="text-muted small">{client.address || "-"}</td>
                      <td>
                        <Badge bg={client.is_active ? "success" : "danger"}>
                          {client.is_active ? "Actif" : "Inactif"}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveClient(client.id)}
                          disabled={isPending}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ManageFieldClients;
